import { RefObject } from 'react';
import { useState, useEffect } from 'react';

/**
 * This hook allows you to easily detect when an element is visible on the screen as well as specify how much of the
 * element should be visible before being considered on screen. Perfect for lazy loading images or triggering
 * animations when the user has scrolled down to a particular section.
 *
 * https://usehooks.com/useOnScreen/
 *
 * @param {*} ref
 * @param {*} rootMargin
 * @returns
 */
export function useOnScreen(
  ref: RefObject<HTMLElement>,
  rootMargin = '0px',
  threshold = 0,
) {
  // State and setter for storing whether element is visible
  const [isIntersecting, setIntersecting] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        // Update our state when observer callback fires
        setIntersecting(Boolean(entry?.isIntersecting));
      },
      {
        rootMargin,
        threshold,
      },
    );

    const { current } = ref || {};
    if (current) {
      observer.observe(current);
    }
    return () => {
      if (current) {
        observer.unobserve(current);
      }
    };
  }, []); // Empty array ensures that effect is only run on mount and unmount
  return isIntersecting;
}
